/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue } from "recoil";
import { NoteAPI } from "../../../api/Note";
import { NoteCategoriesAPI } from "../../../api/NoteCategory";
import { ContactFilterAtom } from "../../../atoms/ContactFilterAtom";
import { ContactFiltersAtom } from "../../../atoms/ContactFiltersAtom";
import { ContactNotesAtom, NoteCategoriesAtom } from "../../../atoms/NotesAtom";
import { INoteType } from "../../../types/NoteCategory";
import { ContactNote } from "../../../types/NoteTypes";
import {
  localStorageGetItem,
  localStorageSetItem,
} from "../../../utils/LocalStorage";
import AutoCompleteNoteCategory from "../../Notes/AutoCompleteNoteCategory";
import StyledButton from "../../StyledButton";
import StyledTextArea from "../../StyledTextArea";
import { useModal } from "../Modal";

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 30px 0;
  width: 400px;
  height: 200px;
`;

const Buttons = styled.div`
  margin: 0 -25px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #dddddd;
  padding: 20px 20px 0px;
`;

type Props =
  | {
      contactId: string;
      contactIds?: string[];
      selectAll?: boolean;
    }
  | {
      contactId?: string;
      contactIds: string[];
      selectAll?: boolean;
    };

export default function CreateUserNote(props: Props): JSX.Element {
  const modal = useModal();
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const [notes, setNotes] = useRecoilState(ContactNotesAtom);

  const lastBlockNoteCategoryName = localStorageGetItem(
    "LAST_NOTE_CATEGORY"
  ) as string;

  const [categoryInputValue, setCategoryInputValue] = useState(
    lastBlockNoteCategoryName || ""
  );

  const [categorySelected, setCategorySelected] = useState<{
    id: string;
    name: string;
    type: string;
  }>({
    id: "",
    name: "",
    type: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<
    { id: string; name: string; type: INoteType }[]
  >([]);
  const [userNote, setUserNote] = useState("");
  const [categories, setCategories] = useRecoilState(NoteCategoriesAtom);
  const contactFilter = useRecoilValue(ContactFilterAtom);
  const contactFilters = useRecoilValue(ContactFiltersAtom);

  const { contactId, contactIds, selectAll } = props;

  useEffect(() => {
    (async () => {
      const categoriesResponse = await NoteCategoriesAPI.readAll();

      if (categoriesResponse) {
        setCategories(categoriesResponse);

        if (lastBlockNoteCategoryName) {
          const foundNoteCategory = categoriesResponse.find(
            (category) => category.name === lastBlockNoteCategoryName
          );
          if (foundNoteCategory) {
            setCategorySelected({
              id: foundNoteCategory._id?.toString() || "",
              name: foundNoteCategory.name,
              type: foundNoteCategory.type,
            });
          }
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (!categories) return;

    const newCategories = categories.map((c) => ({
      id: c._id?.toString() || "",
      name: c.name,
      type: c.type,
    }));

    setSuggestions(newCategories);
  }, [categories]);

  async function createNote() {
    setIsLoading(true);

    if (contactId) {
      try {
        const note = await NoteAPI.createUserNote({
          noteCategoryId: categorySelected.id,
          note: userNote,
          contactId,
        });

        if (note) {
          const newNotes = await NoteAPI.getNotesFromContact(contactId);
          if (!newNotes) return;

          const sortedNotes = newNotes.sort((a, b) => {
            return dayjs(b.createdAt).diff(a.createdAt);
          });

          setNotes(sortedNotes);

          localStorageSetItem("LAST_NOTE_CATEGORY", categorySelected.name);

          toast("Note created", { type: "success" });
          modal.popModal();
        }
      } catch (e) {
        toast("Error on create note", { type: "warning" });
      } finally {
        setIsLoading(false);
      }
    } else if (contactIds) {
      try {
        let note: false | ContactNote;

        if (selectAll) {
          note = await NoteAPI.createUserNoteOnContactsSelected({
            noteCategoryId: categorySelected.id,
            note: userNote,
            query: { ...contactFilters, tagQuery: contactFilter?.tagQuery },
          });
        } else {
          note = await NoteAPI.createUserNoteOnContactsSelected({
            noteCategoryId: categorySelected.id,
            note: userNote,
            contactIds,
          });
        }

        if (note) {
          toast("Notes created", { type: "success" });
          modal.popModal();
        }
      } catch (e) {
        toast("Error on create note", { type: "warning" });
      } finally {
        setIsLoading(false);
      }
    }
  }

  useEffect(() => {
    if (textAreaRef.current) textAreaRef.current.focus();
  }, []);

  async function handleCategoryValueSelect(option: any) {
    if (option) {
      setCategorySelected(option);
      setCategoryInputValue(option.name);
    }
  }

  return (
    <>
      <InputWrapper>
        <Title>Note</Title>
        <StyledTextArea
          ref={textAreaRef}
          value={userNote}
          onChange={(e) => setUserNote(e.target.value)}
          placeholder="Enter the note"
          css={css`
            margin-top: 5px;
          `}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              createNote();
            }
          }}
        />

        <Title
          css={css`
            margin: 20px 0px 5px;
          `}
        >
          Note Category
        </Title>
        <AutoCompleteNoteCategory
          selectedValue={categoryInputValue}
          onValueSelect={(option) => handleCategoryValueSelect(option)}
          onEnter={(option) => handleCategoryValueSelect(option)}
          onValueChange={setCategoryInputValue}
          placeholder="Now enter or select a category for this note"
          suggestions={suggestions}
          onCancel={() => {
            setCategoryInputValue("");
          }}
          optionIsSelected={(() => {
            if (!categorySelected) return false;
            if (categorySelected.name !== categoryInputValue) return false;
            else return true;
          })()}
        />
      </InputWrapper>

      <Buttons
        css={css`
          display: flex;
          justify-content: flex-end;
        `}
      >
        <StyledButton
          inverted
          css={css`
            width: 80px;
            margin-right: 10px;
          `}
          onClick={() => modal.popModal()}
        >
          Cancel
        </StyledButton>
        <StyledButton
          disable={(() => {
            if (categoryInputValue === "") return true;
            if (!categorySelected) return true;
            if (categorySelected.name !== categoryInputValue) return true;
            else return false;
          })()}
          css={css`
            width: 80px;
          `}
          isLoading={isLoading}
          onClick={() => {
            createNote();
          }}
        >
          Save
        </StyledButton>
      </Buttons>
    </>
  );
}
